import { useQuery } from "@tanstack/react-query";
import { Eraser, Play } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getFixCauchy,
  getPuntosCauchy,
  getSerieCauchy,
  llaveCauchy,
} from "@/lib/api";

/**
 * Operación → VaR DART (Montecarlo).
 *
 * Con el location/scale de la Cauchy del DART (etrm.dart_cauchy_diario) de un
 * punto y una fecha de cálculo, simula el DART de cada hora del trading del
 * día de operación, como en la hoja de Excel de riesgos:
 *
 *   DART_h = MAX(LH mín, MIN(LH máx, location + scale·TAN(π·(ALEATORIO() − 0.5))))
 *   PnL    = Σ_h DART_h · −MWh_h  ÷ TC                        (USD; MWh con su signo)
 *            (se pierde cuando el DART es positivo, MDA > MTR)
 *   VaR    = PERCENTIL(PnL de las N simulaciones, 1 − confianza)
 *   CVaR   = PROMEDIO de los PnL estrictamente menores que el VaR
 *
 * PERCENTIL es el de Excel (PERCENTILE.INC: interpolación lineal). Todo corre
 * en el navegador; ALEATORIO no lleva semilla, así que cada corrida varía un
 * poco (con 20 000 simulaciones la diferencia es chica).
 */

const N_SIM = 20000;
const HORAS = Array.from({ length: 24 }, (_, i) => i + 1);

const COLOR_RESTO = "#2a78d6";
const COLOR_COLA = "#eb6834";

const hoyMas = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Número de una celda pegada de Excel: acepta "1,234.5", "1234,5", "$ 12". */
function parseNum(s: string): number {
  let t = s.trim().replace(/[$\s]/g, "");
  if (!t) return NaN;
  if (t.includes(",") && t.includes(".")) t = t.replace(/,/g, "");
  else if (/^-?\d{1,3}(,\d{3})+$/.test(t)) t = t.replace(/,/g, "");
  else t = t.replace(",", ".");
  return Number(t);
}

/** PERCENTIL de Excel (PERCENTILE.INC) sobre un arreglo YA ordenado. */
function percentilInc(ord: Float64Array, p: number): number {
  const h = (ord.length - 1) * p;
  const lo = Math.floor(h);
  const hi = Math.min(lo + 1, ord.length - 1);
  return ord[lo] + (h - lo) * (ord[hi] - ord[lo]);
}

type Resultado = {
  pnl: Float64Array;          // USD por simulación
  varD: number;               // PERCENTIL(PnL, 1 − confianza)
  cvarD: number;              // PROMEDIO.SI.CONJUNTO(PnL, "<" & VaR)
  pctSup: number;             // PERCENTIL(PnL, confianza)
  promSup: number;            // PROMEDIO.SI.CONJUNTO(PnL, ">" & pctSup)
  utilEsp: number;            // PROMEDIO.SI.CONJUNTO(PnL, "<" & pctSup, PnL, ">0")
  perdEsp: number;            // PROMEDIO.SI.CONJUNTO(PnL, ">" & VaR, PnL, "<0")
  media: number;              // Promedio diario
  p50: number;
  volumen: number;            // Σ |MWh| del día
  tc: number;                 // MXN/USD con el que se simuló (equivalente en MXN de las tarjetas)
  dartMedio: number[];        // MXN/MWh por hora
  pnlMedioHora: number[];     // USD por hora
  firma: string;              // entradas con las que se simuló
};

function simular(mwh: number[], loc: number, sc: number, lhMin: number, lhMax: number,
                 tc: number, confianza: number, firma: string): Resultado {
  const pnl = new Float64Array(N_SIM);
  const sumaDart = new Array(24).fill(0);
  for (let s = 0; s < N_SIM; s++) {
    let total = 0;
    for (let h = 0; h < 24; h++) {
      const x = loc + sc * Math.tan(Math.PI * (Math.random() - 0.5));
      const d = Math.max(lhMin, Math.min(lhMax, x));
      sumaDart[h] += d;
      total += d * -mwh[h];
    }
    pnl[s] = total / tc;
  }
  const ord = Float64Array.from(pnl).sort();
  const varD = percentilInc(ord, 1 - confianza);
  const pctSup = percentilInc(ord, confianza);
  let suma = 0, n = 0, sumaSup = 0, nSup = 0, sumaUtil = 0, nUtil = 0, sumaPerd = 0, nPerd = 0, total = 0;
  for (const v of pnl) {
    total += v;
    if (v < varD) { suma += v; n++; }
    if (v > pctSup) { sumaSup += v; nSup++; }
    if (v > 0 && v < pctSup) { sumaUtil += v; nUtil++; }
    if (v < 0 && v > varD) { sumaPerd += v; nPerd++; }
  }
  const dartMedio = sumaDart.map((x) => x / N_SIM);
  return {
    pnl,
    varD,
    cvarD: n ? suma / n : NaN,
    pctSup,
    promSup: nSup ? sumaSup / nSup : NaN,
    utilEsp: nUtil ? sumaUtil / nUtil : NaN,
    perdEsp: nPerd ? sumaPerd / nPerd : NaN,
    media: total / N_SIM,
    p50: percentilInc(ord, 0.5),
    volumen: mwh.reduce((a, b) => a + Math.abs(b), 0),   // Σ |MWh|: divisor de VaR/CVaR por MWh
    tc,
    dartMedio,
    pnlMedioHora: dartMedio.map((d, h) => (d * -mwh[h]) / tc),
    firma,
  };
}

export function VarDartPage() {
  const qp = useQuery({ queryKey: ["cauchy", "puntos"], queryFn: getPuntosCauchy });
  const puntos = qp.data?.puntos ?? [];

  const [punto, setPunto] = useState("");
  const [fechaCalc, setFechaCalc] = useState("");
  const [fechaOp, setFechaOp] = useState(hoyMas(1));
  const [trading, setTrading] = useState<string[]>(() => Array(24).fill(""));
  const [lhMin, setLhMin] = useState("-3500");
  const [lhMax, setLhMax] = useState("3500");
  const [tc, setTc] = useState("");
  const [tcTocado, setTcTocado] = useState(false);
  const [confianza, setConfianza] = useState("97");
  const [res, setRes] = useState<Resultado | null>(null);
  const [corriendo, setCorriendo] = useState(false);

  // Punto por defecto: el primero activo; fecha de cálculo = su último día calculado.
  useEffect(() => {
    if (punto || puntos.length === 0) return;
    const p = puntos.find((x) => x.activo) ?? puntos[0];
    setPunto(llaveCauchy(p));
    setFechaCalc(p.hasta);
  }, [puntos, punto]);

  const infoPunto = puntos.find((p) => llaveCauchy(p) === punto);

  const qParam = useQuery({
    queryKey: ["cauchy", "serie", [punto], fechaCalc, fechaCalc],
    queryFn: () => getSerieCauchy([punto], fechaCalc, fechaCalc),
    enabled: !!punto && !!fechaCalc,
  });
  const param = qParam.data?.serie[0];

  const qFix = useQuery({
    queryKey: ["cauchy", "fix", fechaOp],
    queryFn: () => getFixCauchy(fechaOp),
    enabled: !!fechaOp,
  });
  useEffect(() => {
    if (!tcTocado && qFix.data?.valor) setTc(qFix.data.valor.toFixed(4));
  }, [qFix.data, tcTocado]);

  const mwh = trading.map((t) => parseNum(t));   // con su signo
  const mwhOk = mwh.map((v) => (Number.isFinite(v) ? v : 0));
  const volumen = mwhOk.reduce((a, b) => a + Math.abs(b), 0);   // Σ |MWh|, solo para VaR/CVaR por MWh
  const nLmin = parseNum(lhMin), nLmax = parseNum(lhMax), nTc = parseNum(tc);
  const nConf = parseNum(confianza) / 100;

  const errores: string[] = [];
  if (!param) errores.push("No hay location/scale para ese punto y fecha de cálculo.");
  if (!Number.isFinite(nLmin) || !Number.isFinite(nLmax) || nLmin >= nLmax)
    errores.push("LH Mín DART debe ser menor que LH Máx DART.");
  if (!(nTc > 0)) errores.push("Captura un tipo de cambio mayor que cero.");
  if (!(nConf > 0 && nConf < 1)) errores.push("La confianza debe estar entre 0 y 100 %.");
  if (trading.some((t) => t.trim() !== "" && !Number.isFinite(parseNum(t))))
    errores.push("Hay horas del trading que no son número.");
  if (volumen === 0) errores.push("Captura o pega el trading de las 24 horas.");

  const firma = JSON.stringify([punto, fechaCalc, param?.location, param?.scale, mwhOk, nLmin, nLmax, nTc, nConf]);
  const desactualizado = res !== null && res.firma !== firma;

  const correr = () => {
    if (errores.length || !param) return;
    setCorriendo(true);
    // Deja pintar el "Simulando…" antes del cálculo.
    setTimeout(() => {
      setRes(simular(mwhOk, param.location, param.scale, nLmin, nLmax, nTc, nConf, firma));
      setCorriendo(false);
    }, 20);
  };

  // Pegar una columna de Excel en cualquier hora llena desde esa hora en adelante.
  const pegar = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    const vals = e.clipboardData.getData("text").split(/[\r\n\t]+/).map((s) => s.trim()).filter(Boolean);
    if (vals.length <= 1) return;
    e.preventDefault();
    setTrading((prev) => {
      const nuevo = [...prev];
      vals.slice(0, 24 - i).forEach((v, k) => {
        const n = parseNum(v);
        nuevo[i + k] = Number.isFinite(n) ? String(n) : v;
      });
      return nuevo;
    });
  };

  return (
    <div className="space-y-5 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">VaR DART (Montecarlo)</h1>
        <p className="text-sm text-slate-500 mt-1">
          Simula {N_SIM.toLocaleString("es-MX")} veces el DART de cada hora del trading con la
          Cauchy del punto (location y scale de la fecha de cálculo), acotado entre LH Mín y LH
          Máx, y calcula el VaR y CVaR del PnL diario en USD.
        </p>
      </div>

      {qp.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(qp.error as Error).message}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[22rem_1fr]">
        {/* Entradas */}
        <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-4 self-start">
          <Campo etiqueta="Nodo / zona">
            <select
              value={punto}
              onChange={(e) => {
                const p = puntos.find((x) => llaveCauchy(x) === e.target.value);
                setPunto(e.target.value);
                if (p) setFechaCalc(p.hasta);
              }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              {puntos.map((p) => (
                <option key={llaveCauchy(p)} value={llaveCauchy(p)}>
                  {p.etiqueta} · {p.tipo === "zona" ? "Zona (PND)" : "Nodo (PML)"} · {p.clave}
                </option>
              ))}
            </select>
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Fecha de cálculo" ayuda={infoPunto ? `último: ${fechaCorta(infoPunto.hasta)}` : undefined}>
              <input
                type="date"
                value={fechaCalc}
                min={infoPunto?.desde}
                max={infoPunto?.hasta}
                onChange={(e) => setFechaCalc(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </Campo>
            <Campo etiqueta="Fecha de operación">
              <input
                type="date"
                value={fechaOp}
                onChange={(e) => setFechaOp(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </Campo>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded bg-slate-50 px-3 py-2">
            <Dato etiqueta="Location" valor={param ? num(param.location, 2) : "—"} />
            <Dato etiqueta="Scale" valor={param ? num(param.scale, 2) : "—"} />
            {qParam.isSuccess && !param && (
              <div className="col-span-2 text-xs text-amber-700">Sin cálculo para esa fecha.</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="LH Mín DART (MXN/MWh)">
              <input value={lhMin} onChange={(e) => setLhMin(e.target.value)} inputMode="decimal"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums" />
            </Campo>
            <Campo etiqueta="LH Máx DART (MXN/MWh)">
              <input value={lhMax} onChange={(e) => setLhMax(e.target.value)} inputMode="decimal"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums" />
            </Campo>
            <Campo etiqueta="Tipo de cambio (MXN/USD)"
              ayuda={!tcTocado && qFix.data?.fecha ? `FIX del ${fechaCorta(qFix.data.fecha)}` : tcTocado ? "capturado" : undefined}>
              <input value={tc} onChange={(e) => { setTc(e.target.value); setTcTocado(true); }} inputMode="decimal"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums" />
            </Campo>
            <Campo etiqueta="Confianza (%)">
              <input value={confianza} onChange={(e) => setConfianza(e.target.value)} inputMode="decimal"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums" />
            </Campo>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-slate-600">Trading del día (MWh)</div>
              <button type="button" onClick={() => setTrading(Array(24).fill(""))}
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700">
                <Eraser className="h-3.5 w-3.5" /> Limpiar
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-2">
              Pega la columna de 24 horas de Excel en la hora 1 (o en cualquier hora para llenar desde ahí).
            </p>
            <div className="grid grid-cols-3 gap-x-3 gap-y-1">
              {HORAS.map((h, i) => (
                <label key={h} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="w-5 text-right tabular-nums">{h}</span>
                  <input
                    value={trading[i]}
                    onChange={(e) => setTrading((p) => p.map((v, k) => (k === i ? e.target.value : v)))}
                    onBlur={() => {
                      const n = parseNum(trading[i]);
                      if (Number.isFinite(n)) setTrading((p) => p.map((v, k) => (k === i ? String(n) : v)));
                    }}
                    onPaste={(e) => pegar(i, e)}
                    inputMode="decimal"
                    className="w-full min-w-0 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-right tabular-nums text-slate-800"
                  />
                </label>
              ))}
            </div>
            <div className="mt-2 text-xs text-slate-500 text-right">
              Σ |MWh|: <span className="font-medium text-slate-700 tabular-nums">{num(volumen, 2)} MWh</span>
            </div>
          </div>

          {errores.length > 0 && (
            <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
              {errores.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}

          <button
            type="button"
            onClick={correr}
            disabled={errores.length > 0 || corriendo}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            {corriendo ? "Simulando…" : res ? "Volver a simular" : `Simular ${N_SIM.toLocaleString("es-MX")}`}
          </button>
        </section>

        {/* Resultados */}
        <div className="space-y-5 min-w-0">
          {!res ? (
            <section className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-400">
              Captura el trading y presiona Simular.
            </section>
          ) : (
            <Resultados res={res} confianza={nConf} desactualizado={desactualizado}
              fechaOp={fechaOp} etiqueta={infoPunto?.etiqueta ?? ""} mwh={mwhOk} />
          )}
        </div>
      </div>
    </div>
  );
}

function Resultados({ res, confianza, desactualizado, fechaOp, etiqueta, mwh }: {
  res: Resultado;
  confianza: number;
  desactualizado: boolean;
  fechaOp: string;
  etiqueta: string;
  mwh: number[];
}) {
  // Histograma del PnL: 50 clases entre los percentiles 0.5 y 99.5 (las colas
  // de la Cauchy estiran el rango y aplastarían el centro); lo que cae fuera se
  // acumula en la primera / última clase.
  const hist = useMemo(() => {
    const ord = Float64Array.from(res.pnl).sort();
    const lo = percentilInc(ord, 0.005), hi = percentilInc(ord, 0.995);
    const nb = 50;
    const ancho = (hi - lo) / nb || 1;
    const cuentas = new Array(nb).fill(0);
    for (const v of res.pnl) cuentas[Math.max(0, Math.min(nb - 1, Math.floor((v - lo) / ancho)))]++;
    return cuentas.map((c, i) => {
      const desde = lo + i * ancho;
      return {
        i,
        desde: i === 0 ? -Infinity : desde,
        hasta: i === nb - 1 ? Infinity : desde + ancho,
        centro: desde + ancho / 2,
        pct: (100 * c) / res.pnl.length,
      };
    });
  }, [res]);
  // Clase donde caen el VaR y el CVaR (las líneas de referencia van sobre el eje de clases).
  const claseDe = (v: number) => hist.find((b) => v >= b.desde && v < b.hasta)?.i;
  const iVar = claseDe(res.varD);
  const iCvar = Number.isFinite(res.cvarD) ? claseDe(res.cvarD) : undefined;
  const iSup = claseDe(res.pctSup);
  const pctConf = Math.round(confianza * 1000) / 10;

  return (
    <>
      {desactualizado && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-2.5 text-sm">
          Cambiaron las entradas: estos resultados son de la simulación anterior. Vuelve a simular.
        </div>
      )}

      <TarjetasVar v={res} confianza={confianza} />
      <div className="text-xs text-slate-500 -mt-2 space-y-0.5">
        <p>
          {etiqueta} · operación {fechaCorta(fechaOp)} · {N_SIM.toLocaleString("es-MX")} simulaciones ·
          trading {num(res.volumen, 2)} MWh (suma de valores absolutos). Montos en USD.
        </p>
        <p>
          VaR = percentil {num(100 - pctConf, 1)} % del PnL · CVaR = promedio de los PnL menores que
          el VaR · Percentil {pctConf} % = percentil {pctConf} % del PnL · Promedio &gt; Percentil =
          promedio de los PnL mayores que ese percentil · Utilidad esperada = promedio de los PnL
          mayores que 0 y menores que el Percentil {pctConf} % · Pérdida esperada = promedio de los
          PnL menores que 0 y mayores que el VaR · Utilidad neta = Utilidad esperada + Pérdida
          esperada · por MWh = valor ÷ {num(res.volumen, 2)} MWh.
        </p>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-sm font-medium text-slate-800">Distribución del PnL diario (USD)</div>
        <div className="text-xs text-slate-500 mb-2">
          % de simulaciones por rango (del percentil 0.5 al 99.5; las colas se suman en las
          barras de los extremos). En naranja, la cola por debajo del VaR.
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hist} margin={{ top: 18, right: 24, bottom: 0, left: 4 }} barCategoryGap={1}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="i"
                type="category"
                interval={9}
                tickFormatter={(i: number) => usdCorto(hist[i]?.centro ?? 0)}
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickLine={false}
                axisLine={{ stroke: "#e2e8f0" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={(v: number) => `${num(v, 1)} %`}
              />
              <Tooltip
                cursor={{ fill: "#f1f5f9" }}
                formatter={(v: number) => [`${num(v, 2)} %`, "Simulaciones"]}
                labelFormatter={(_, p) => {
                  const d = p?.[0]?.payload as { desde: number; hasta: number } | undefined;
                  if (!d) return "";
                  if (!Number.isFinite(d.desde)) return `menos de ${usd(d.hasta)}`;
                  if (!Number.isFinite(d.hasta)) return `${usd(d.desde)} o más`;
                  return `${usd(d.desde)} a ${usd(d.hasta)}`;
                }}
                contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }}
              />
              <Bar dataKey="pct" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                {hist.map((b) => (
                  <Cell key={b.i} fill={b.centro < res.varD ? COLOR_COLA : COLOR_RESTO} />
                ))}
              </Bar>
              {iVar !== undefined && (
                <ReferenceLine x={iVar} stroke="#334155" strokeDasharray="4 3"
                  label={{ value: "VaR", position: "top", fontSize: 11, fill: "#334155" }} />
              )}
              {iCvar !== undefined && iCvar !== iVar && (
                <ReferenceLine x={iCvar} stroke="#334155"
                  label={{ value: "CVaR", position: "top", fontSize: 11, fill: "#334155" }} />
              )}
              {iSup !== undefined && (
                <ReferenceLine x={iSup} stroke="#334155" strokeDasharray="4 3"
                  label={{ value: `P${pctConf}`, position: "top", fontSize: 11, fill: "#334155" }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-medium text-slate-800">
          Por hora (promedio de las simulaciones)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-2">Hora</th>
                <th className="text-right font-medium px-3 py-2">Trading (MWh)</th>
                <th className="text-right font-medium px-3 py-2">DART medio (MXN/MWh)</th>
                <th className="text-right font-medium px-4 py-2">PnL medio (USD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {HORAS.map((h, i) => (
                <tr key={h}>
                  <td className="px-4 py-1 text-slate-600 tabular-nums">{h}</td>
                  <td className="px-3 py-1 text-right tabular-nums text-slate-700">{num(mwh[i], 2)}</td>
                  <td className="px-3 py-1 text-right tabular-nums text-slate-700">{num(res.dartMedio[i], 2)}</td>
                  <td className="px-4 py-1 text-right tabular-nums text-slate-700">{usd(res.pnlMedioHora[i])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** Tarjetas de resultados (USD, con su equivalente en MXN al TC simulado).
 *  También las usa /arbitraje para mostrar el VaR guardado de cada día. */
export type ValoresVar = {
  varD: number; cvarD: number; pctSup: number; promSup: number;
  utilEsp: number; perdEsp: number; media: number; p50: number;
  volumen: number; tc: number;
};

export function TarjetasVar({ v, confianza }: { v: ValoresVar; confianza: number }) {
  const pctConf = Math.round(confianza * 1000) / 10;
  const enMxn = (vUsd: number, sufijo = "") => `${usd(vUsd * v.tc)} MXN${sufijo}`;
  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Tile etiqueta={`VaR diario (${pctConf} %)`} valor={usd(v.varD)} unidad="USD" mxn={enMxn(v.varD)} fuerte />
        <Tile etiqueta="CVaR diario" valor={usd(v.cvarD)} unidad="USD" mxn={enMxn(v.cvarD)} fuerte />
        <Tile etiqueta="VaR diario por MWh" valor={usd(v.varD / v.volumen)} unidad="USD/MWh"
          mxn={enMxn(v.varD / v.volumen, "/MWh")} />
        <Tile etiqueta="CVaR diario por MWh" valor={usd(v.cvarD / v.volumen)} unidad="USD/MWh"
          mxn={enMxn(v.cvarD / v.volumen, "/MWh")} />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Tile etiqueta={`Percentil ${pctConf} %`} valor={usd(v.pctSup)} unidad="USD" mxn={enMxn(v.pctSup)} />
        <Tile etiqueta={`Promedio > Percentil ${pctConf} %`} valor={usd(v.promSup)} unidad="USD" mxn={enMxn(v.promSup)} />
        <Tile etiqueta="Utilidad esperada diaria" valor={usd(v.utilEsp)} unidad="USD" mxn={enMxn(v.utilEsp)} />
        <Tile etiqueta="Pérdida esperada diaria" valor={usd(v.perdEsp)} unidad="USD" mxn={enMxn(v.perdEsp)} />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Tile etiqueta="Utilidad neta diaria" valor={usd(v.utilEsp + v.perdEsp)} unidad="USD"
          mxn={enMxn(v.utilEsp + v.perdEsp)} />
        <Tile etiqueta="Promedio diario" valor={usd(v.media)} unidad="USD" mxn={enMxn(v.media)} />
        <Tile etiqueta="PnL mediano" valor={usd(v.p50)} unidad="USD" mxn={enMxn(v.p50)} />
      </div>
    </section>
  );
}

function Campo({ etiqueta, ayuda, children }: { etiqueta: string; ayuda?: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-slate-500 space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-slate-600">{etiqueta}</span>
        {ayuda && <span className="text-[11px] text-slate-400">{ayuda}</span>}
      </div>
      {children}
    </label>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{etiqueta} (MXN/MWh)</div>
      <div className="text-sm font-medium text-slate-800 tabular-nums">{valor}</div>
    </div>
  );
}

function Tile({ etiqueta, valor, unidad, mxn, fuerte = false }: {
  etiqueta: string;
  valor: string;
  unidad?: string;
  mxn?: string;               // equivalente en pesos, en pequeño bajo el valor
  fuerte?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
      <div className="text-xs text-slate-500">
        {etiqueta}
        {unidad && <span className="text-slate-400"> · {unidad}</span>}
      </div>
      <div className={`tabular-nums mt-0.5 ${fuerte ? "text-xl font-semibold text-slate-900" : "text-lg text-slate-800"}`}>
        {valor}
      </div>
      {mxn && <div className="text-xs text-slate-400 tabular-nums mt-0.5">{mxn}</div>}
    </div>
  );
}

function num(v: number, dec: number): string {
  return Number.isFinite(v)
    ? v.toLocaleString("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec })
    : "—";
}
function usd(v: number): string {
  return Number.isFinite(v) ? `${v < 0 ? "−" : ""}$${num(Math.abs(v), 2)}` : "—";
}
function usdCorto(v: number): string {
  const a = Math.abs(v);
  const s = a >= 1e6 ? `${num(a / 1e6, 1)}M` : a >= 1e3 ? `${num(a / 1e3, 0)}k` : num(a, 0);
  return `${v < 0 ? "−" : ""}$${s}`;
}
function fechaCorta(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}
